import * as React from 'react';
import { FavouriteLayoutComponentProps, FavouriteLayoutAttributes } from './types';
import { ChartFavouriteModelComponent } from './favourite/common/chart-favourite-model-component';
import { ChartLayoutConfig } from './favourite/common/layout-config';
import { createReactChartFavouriteModelComponent } from './favourite/react/react-favourite-model-component-factory';
import { chartType, ColumnType } from './constants';
import { AggregationType } from 'app/components/favourite-layout/favourite/common/favourite-interfaces';
import { MIN_NO_OF_CRITERIAS } from 'app/area/reports/containers/chart/components/attributes/constants';
import { reportTypes } from 'app/area/favourite/constants';

export class ChartFavourite extends React.Component<FavouriteLayoutComponentProps> {
  private model: ChartFavouriteModelComponent;
  //@ts-ignore
  private ref: HTMLElement;
  constructor(props: FavouriteLayoutComponentProps) {
    super(props);
    this.model = this.prepareModel(props.layoutAttributes, props.intl, props.translation);
  }

  private prepareModel(layoutAttributes: FavouriteLayoutAttributes, intl: any, translation: any) {
    return createReactChartFavouriteModelComponent(
      layoutAttributes,
      this.props.drillDown,
      this.props.caseview,
      intl,
      translation,
      null,
      this.props.numberOfGroupByFields
    );
  }

  private expandWidth = (props: FavouriteLayoutComponentProps) => {
    const { favouriteDataModel } = props;
    const isRiskMatrix = !!props.layoutAttributes['HighchartsStyle.heatmapUseDataPointColors'];

    if (this.getChartTypeFromConfig() === chartType.HEATMAP
      && favouriteDataModel
      && this.props.requestCustomWidth
      && !isRiskMatrix) {

      this.props.requestCustomWidth(favouriteDataModel.id, Math.ceil(favouriteDataModel.rows.length / 15) * 500);
    }
  }

  private beforePrint = () => {
    this.ref.classList.add('printing');
    this.model.reflowChart();
  }

  private afterPrint = () => {
    this.ref.classList.remove('printing');
    this.model.reflowChart();
  }

  public componentDidMount() {
    if (this.isChartTypeSupported() && this.props.favouriteDataModel) {
      this.model.receiveData(this.props.favouriteDataModel);
      this.expandWidth(this.props);
    }
    window.addEventListener('beforeprint', this.beforePrint);
    window.addEventListener('afterprint', this.afterPrint);
  }

  public componentDidUpdate(prevProps: FavouriteLayoutComponentProps) {
    let dirty: boolean = false;
    if (this.props.layoutAttributes) {
      if (prevProps.layoutAttributes !== this.props.layoutAttributes) {
        this.model.receiveLayout(this.props.layoutAttributes);
        dirty = true;
      }

      if (!this.props.fetchingDataModel && prevProps.favouriteDataModel !== this.props.favouriteDataModel) {

        this.expandWidth(this.props);
        this.model.receiveData(this.props.favouriteDataModel);
        dirty = true;
      }
    }

    if (prevProps.reflowHandle !== this.props.reflowHandle) {
      this.model.reflowChart();
    }

    if (dirty) {
      this.forceUpdate();
    }
  }

  public componentWillUnmount() {
    if (this.model) {
      //Destroy chart before unmount.
      this.model.destroy();
    }
    window.removeEventListener('beforeprint', this.beforePrint);
    window.removeEventListener('afterprint', this.afterPrint);
  }

  private getChartTypeFromConfig() {
    return (this.model.config as ChartLayoutConfig).chartType();
  }

  private isChartTypeSupported() {
    switch (this.getChartTypeFromConfig()) {
      case chartType.COLUMN:
      case chartType.PIE:
      case chartType.LINE:
      case chartType.BAR:
      case chartType.HEATMAP:
        return true;
      default:
        return false;
    }
  }

  private onChartElementReady = (element: HTMLElement) => {
    //If a render has happened, this might be null for older callbacks.
    if (element) {
      this.ref = element;
      this.model.setChartElement(element);
    }
  }

  private showUnsupportedChartWarning = () => {
    const { layoutAttributes } = this.model.config;
    const { favouriteDataModel, translation, numberOfGroupByFields } = this.props;
    const textBarColumnPercentWarning = translation.column_chart_percentage_message;
    //396175 - TODO - The texts used in those have been hard coded in 16.32. We need to add texts to the database in 16.33
    const textAverageMinimumMaximumWarning = 'Percent is not available for Minimum, Maximum and Average. Please remove \'Percent\' option or select another value for \'How to aggregate\'.';
    const textDaysSinceAggrWarning = 'Percent is not available for Days since first and Days since last. Please remove \'Percent\' option.';
    const textRollingAverageWarning = 'Percent is not available for Rolling average. Please remove \'Percent\' option or clear rolling average field.';
    const textTargetWarning = 'Percent is not available for Target. Please remove \'Percent\' option or clear target field.';
    const textFrequencyReportPercentWarning = 'Percent is not available for frequency reports. Please remove \'Percent\' option.';

    const averageMinimumMaximumAggrTypesEnabled = favouriteDataModel?.columns.some(
      c => c.columnType === ColumnType.aggregation &&
        (c.aggregationType === AggregationType.Average ||
          c.aggregationType === AggregationType.Maximum ||
          c.aggregationType === AggregationType.Minimum));

    const daysSinceAggrTypesEnabled = favouriteDataModel?.columns.some(c => c.columnType === ColumnType.aggregation &&
      (c.aggregationType === AggregationType.DaysSinceFirst ||
        c.aggregationType === AggregationType.DaysSinceLast));

    const hasRollingAverage = favouriteDataModel?.columns.some(c => c.columnType === ColumnType.aggregationRollAvg);
    const hasTarget = favouriteDataModel?.columns.some(c => c.columnType === ColumnType.target);
    const enoughSelectionCriterias = numberOfGroupByFields >= MIN_NO_OF_CRITERIAS;
    const isFrequencyReport = favouriteDataModel?.report === reportTypes.frequencyReport;

    if (layoutAttributes.Column?.Percent && !layoutAttributes.Column?.Stacked ||
      layoutAttributes.Bar?.Percent && !layoutAttributes.Bar?.Stacked) {
      return this.renderWarning(textBarColumnPercentWarning);
    } else if (enoughSelectionCriterias && layoutAttributes.Bar?.Percent && layoutAttributes.Bar?.Stacked ||
      enoughSelectionCriterias && layoutAttributes.Column?.Percent && layoutAttributes.Column?.Stacked) {
      if (isFrequencyReport) {
        return this.renderWarning(textFrequencyReportPercentWarning);
      } else if (averageMinimumMaximumAggrTypesEnabled) {
        return this.renderWarning(textAverageMinimumMaximumWarning);
      } else if (daysSinceAggrTypesEnabled) {
        return this.renderWarning(textDaysSinceAggrWarning);
      } else if (hasRollingAverage) {
        return this.renderWarning(textRollingAverageWarning);
      } else if (hasTarget) {
        return this.renderWarning(textTargetWarning);
      }
    }

    return null;
  }

  private renderWarning = (textWarning: string) => {
    return <div className="alert alert-warning"><p>{textWarning}</p></div>;
  }

  public render() {
    return (
      this.isChartTypeSupported()
        ? <div>
          {this.showUnsupportedChartWarning()}
          <div ref={this.onChartElementReady} />
        </div>
        : <div>{this.props.translation.chart_not_supported_type}</div>
    );
  }
}
